import { Building } from '../../../../types/protocols';
import { BaseComponentImpl } from '../../../../types/implementations';
import { ComponentLifetime } from '../../TemplatesEditor/types';
import { wellFormedComponents } from '../../../../Helpers/componentIntegrity';

/**
 * Validation result for a single component type's config
 */
export interface ConfigValidationResult {
    hasExpectedLifetime: boolean;
    hasReplacementCost: boolean;
    expectedLifetimeYears?: number;
    replacementCost?: number;
}

/**
 * Component type with validation status
 */
export interface InvalidConfigType {
    componentType: string;
    typeName: string;
    validation: ConfigValidationResult;
    componentCount: number; // How many components use this type
    components: Array<{ key: string; data: any }>; // Components using this type
}

/**
 * Validates a component type's lifetime config for expected lifetime and replacement cost
 */
export function validateConfigLifetime(lifetime: ComponentLifetime | undefined): ConfigValidationResult {
    if (!lifetime) {
        return {
            hasExpectedLifetime: false,
            hasReplacementCost: false
        };
    }

    return {
        hasExpectedLifetime: !!(
            lifetime.expectedLifetimeYears !== undefined && 
            lifetime.expectedLifetimeYears !== null && 
            lifetime.expectedLifetimeYears > 0
        ),
        hasReplacementCost: !!(
            lifetime.replacementCost !== undefined &&
            lifetime.replacementCost !== null &&
            lifetime.replacementCost >= 0
        ),
        expectedLifetimeYears: lifetime.expectedLifetimeYears,
        replacementCost: lifetime.replacementCost
    };
}

/**
 * Gets all component types used in the building that have incomplete config
 */
export function getInvalidConfigTypes(
    building: Building | null,
    getLifetime: (key: string) => ComponentLifetime | undefined,
    getTranslation: (key: string | undefined) => string
): InvalidConfigType[] {
    if (!building?.model?.structure?.components) {
        return [];
    }

    // Count components by type and collect components
    const typeUsage: Record<string, { count: number; components: Array<{ key: string; data: any }> }> = {};
    
    wellFormedComponents(building.model.structure.components).forEach(componentData => {
        const component = new BaseComponentImpl(componentData);
        const specificTypeValue = component.specificType?.value;
        if (specificTypeValue) {
            if (!typeUsage[specificTypeValue]) {
                typeUsage[specificTypeValue] = { count: 0, components: [] };
            }
            typeUsage[specificTypeValue].count += 1;
            typeUsage[specificTypeValue].components.push({
                key: componentData.key,
                data: componentData
            });
        }
    });

    const invalidTypes: InvalidConfigType[] = [];

    // Check each type used in the building
    Object.entries(typeUsage).forEach(([componentType, { count, components }]) => {
        const lifetime = getLifetime(componentType);
        const validation = validateConfigLifetime(lifetime);
        
        // Only include if missing either field
        if (!validation.hasExpectedLifetime || !validation.hasReplacementCost) {
            invalidTypes.push({
                componentType,
                typeName: getTranslation(componentType),
                validation,
                componentCount: count,
                components
            });
        }
    });

    return invalidTypes;
}

/**
 * Checks if all component types used in a building have complete config
 */
export function hasAllConfigsValid(
    building: Building | null,
    getLifetime: (key: string) => ComponentLifetime | undefined
): boolean {
    if (!building?.model?.structure?.components) {
        return true; // No components = valid by default
    }

    // Get all unique component types used
    const typesUsed = new Set<string>();
    wellFormedComponents(building.model.structure.components).forEach(componentData => {
        const component = new BaseComponentImpl(componentData);
        if (component.specificType?.value) {
            typesUsed.add(component.specificType.value);
        }
    });

    // Check if all types have valid config
    for (const componentType of Array.from(typesUsed)) {
        const lifetime = getLifetime(componentType);
        const validation = validateConfigLifetime(lifetime);
        
        if (!validation.hasExpectedLifetime || !validation.hasReplacementCost) {
            return false;
        }
    }

    return true;
}

/**
 * Gets statistics about config validation
 */
export function getConfigValidationStats(
    building: Building | null,
    getLifetime: (key: string) => ComponentLifetime | undefined
): {
    totalTypes: number;
    validTypes: number;
    missingLifetime: number;
    missingCost: number;
    missingBoth: number;
} {
    if (!building?.model?.structure?.components) {
        return {
            totalTypes: 0,
            validTypes: 0,
            missingLifetime: 0,
            missingCost: 0,
            missingBoth: 0
        };
    }

    // Get all unique component types used
    const typesUsed = new Set<string>();
    wellFormedComponents(building.model.structure.components).forEach(componentData => {
        const component = new BaseComponentImpl(componentData);
        if (component.specificType?.value) {
            typesUsed.add(component.specificType.value);
        }
    });

    let validTypes = 0;
    let missingLifetime = 0;
    let missingCost = 0;
    let missingBoth = 0;

    typesUsed.forEach(componentType => {
        const lifetime = getLifetime(componentType);
        const validation = validateConfigLifetime(lifetime);
        
        if (!validation.hasExpectedLifetime && !validation.hasReplacementCost) {
            missingBoth++;
        } else if (!validation.hasExpectedLifetime) {
            missingLifetime++;
        } else if (!validation.hasReplacementCost) {
            missingCost++;
        } else {
            validTypes++;
        }
    });

    return {
        totalTypes: typesUsed.size,
        validTypes,
        missingLifetime,
        missingCost,
        missingBoth
    };
}
